import Transition, {
  TransitionVariant
} from '@aurora/shared-client/components/common/Transition/Transition';
import { isMentionsElement } from '@aurora/shared-client/helpers/editor/EditorElementHelper';
import { isAnchor } from '@aurora/shared-client/helpers/editor/EditorLinkHelperInternal';
import { canUseDOM } from 'exenv';
import type { MutableRefObject } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { Overlay } from 'react-bootstrap';
import useContextObjectFromPath from '../../context/useContextObjectFromPath';
import type { ExtendedOverlayInjectedProps } from '../../extendedOverlayTrigger/ExtendedOverlayTrigger/ExtendedOverlayTrigger';
import {
  HoverCardItemType,
  hoverCardPopperConfig
} from '../../extendedOverlayTrigger/HoverCardHelper';
import NodeHoverCard from '../../nodes/NodeHoverCard/NodeHoverCard';
import UserHoverCard from '../../users/UserHoverCard/UserHoverCard';
import MessageHoverCard from '../MessageHoverCard/MessageHoverCard';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';

export interface MessageBodyLinkHoverCardsProps {
  /**
   * Element ref which contains the static HTML where the hover links will be applied.
   */
  elementRef: MutableRefObject<HTMLElement>;
}

/**
 * Applies hover cards to anchor elements in static markup inside the provided element ref.
 * This is useful for applying hover cards to message bodies that are not rendered using react elements.
 * This largely follows the logic in `ExtendedOverlayTrigger`.
 *
 * @param elementRef the element ref
 *
 * @author Adam Ayres
 */
const MessageBodyLinkHoverCards: React.FC<
  React.PropsWithChildren<MessageBodyLinkHoverCardsProps>
> = ({ elementRef }) => {
  const { getContextObject } = useContextObjectFromPath(module);
  const hoverCardRef = useRef<HTMLElement>(null);
  const inPopoverRef = useRef(false);
  const inTriggerRef = useRef(false);
  const { router } = useEndUserRoutes();

  const [show, setShow] = useState(false);
  const [data, setData] = useState(null);

  const showDelay = 400;
  const hideDelay = 200;

  function handleToggle(updatedShow = false) {
    setShow(updatedShow || inPopoverRef.current || inTriggerRef.current);
  }

  async function handleShow(url: string): Promise<void> {
    const updatedData = await getContextObject(url);
    setData(updatedData);

    setTimeout(() => {
      handleToggle(true);
    }, showDelay);
  }

  function handleHide() {
    setTimeout(() => {
      handleToggle(false);
    }, hideDelay);
  }

  function toggleRef(ref, state) {
    ref.current = state;
  }

  function handlePopoverMouseLeave() {
    toggleRef(inPopoverRef, false);
    setTimeout(() => {
      handleToggle();
    }, hideDelay);
  }

  function handleMouseEnter(event: MouseEvent) {
    const element = event.target as HTMLElement;
    if (
      isAnchor(element) &&
      (isMentionsElement(element) || router.isInternalRoute(element.getAttribute('href')))
    ) {
      if (hoverCardRef.current !== element) {
        setData(null);
        setShow(false);
      }
      hoverCardRef.current = element;
      toggleRef(inTriggerRef, true);
      handleShow(element.getAttribute('href'));
    }
  }

  function handleMouseLeave() {
    toggleRef(inTriggerRef, false);
    handleHide();
  }

  useEffect(() => {
    const messageBodyElement = elementRef.current;

    if (messageBodyElement) {
      messageBodyElement.addEventListener('mouseover', handleMouseEnter);
      messageBodyElement.addEventListener('mouseout', handleMouseLeave);
    }

    return () => {
      messageBodyElement.removeEventListener('mouseover', handleMouseEnter);
      messageBodyElement.removeEventListener('mouseout', handleMouseLeave);
    };
  });

  function getHoverCardComponent(): React.ComponentType<
    ExtendedOverlayInjectedProps & { show: boolean }
  > {
    if (data) {
      const { __typename } = data;
      switch (__typename) {
        case HoverCardItemType.USER: {
          return UserHoverCard;
        }
        case HoverCardItemType.FORUM_REPLY_MESSAGE:
        case HoverCardItemType.TKB_REPLY_MESSAGE:
        case HoverCardItemType.BLOG_REPLY_MESSAGE:
        case HoverCardItemType.IDEA_MESSAGE:
        case HoverCardItemType.FORUM_TOPIC_MESSAGE:
        case HoverCardItemType.TKB_TOPIC_MESSAGE:
        case HoverCardItemType.BLOG_TOPIC_MESSAGE:
        case HoverCardItemType.IDEA_TOPIC_MESSAGE:
        case HoverCardItemType.OCCASION_TOPIC_MESSAGE:
        case HoverCardItemType.ACCEPTED_SOLUTION_MESSAGE: {
          return MessageHoverCard;
        }

        case HoverCardItemType.BOARD:
        case HoverCardItemType.CATEGORY:
        case HoverCardItemType.GROUP_HUB:
        case HoverCardItemType.FORUM:
        case HoverCardItemType.BLOG:
        case HoverCardItemType.IDEA:
        case HoverCardItemType.TKB:
        case HoverCardItemType.OCCASION: {
          return NodeHoverCard;
        }
        default: {
          return null;
        }
      }
    }
    return null;
  }

  const HoverCardComponent = getHoverCardComponent();

  function getHoverCardPlacement(): 'top' | 'bottom' {
    return canUseDOM && hoverCardRef.current?.getBoundingClientRect().y > window?.innerHeight / 2
      ? 'top'
      : 'bottom';
  }

  const CustomTransition = ({ children }) => (
    <Transition variant={TransitionVariant.FADE_SLOW} in={show} appear unmountOnExit>
      {children}
    </Transition>
  );

  if (HoverCardComponent === null) {
    return null;
  }

  return (
    <Overlay
      show={show}
      target={hoverCardRef.current}
      placement={getHoverCardPlacement()}
      popperConfig={hoverCardPopperConfig()}
      transition={CustomTransition}
    >
      {({ style, ref }) => (
        <HoverCardComponent
          entity={data}
          style={style}
          ref={ref}
          onMouseEnter={() => toggleRef(inPopoverRef, true)}
          onMouseLeave={() => handlePopoverMouseLeave()}
          show={show}
        />
      )}
    </Overlay>
  );
};

export default MessageBodyLinkHoverCards;
